
import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEO } from '@/components/seo/SEO';
import ChallengeHero from '@/components/challenge/HeroSection';
import AboutSection from '@/components/challenge/AboutSection';
import HowItWorksSection from '@/components/challenge/HowItWorksSection';
import ParticipationSection from '@/components/challenge/ParticipationSection';
import StatisticsSection from '@/components/challenge/StatisticsSection';
import { Toaster } from "@/components/ui/toaster";
import TestimonialsSection from '@/components/challenge/TestimonialsSection';
import { clipsApi } from '@/api/clips';
import { ContentSection } from '@/components/home/SectionContainers';
import ContentCarousel from '@/components/home/ContentCarousel';
import ClipCard from '@/components/home/ClipCard';
import { useClipsFormatter } from '@/hooks/useClipsFormatter';
import { Film } from 'lucide-react';
import { toast } from 'sonner';
import SimpleDerivativesShowcase from '@/components/challenge/SimpleDerivativesShowcase';

const LAMOUR_SURCOTE_PLAYLIST_ID = '6eca71d9-371e-4223-8e96-478d9a38a0a8';

const prefetchCriticalAssets = () => {
  try {
    const imagePreload = new Image();
    imagePreload.src = "/lovable-uploads/0259c719-1daa-46b5-bd60-cf6e7ee472b9.png";
    
    const logoPreload = new Image();
    logoPreload.src = "/lovable-uploads/dafac05b-8f8c-4fd5-bcb4-507da388ac21.png";
    
    console.log("✅ Images préchargées avec succès");
  } catch (error) {
    console.error("❌ Erreur lors du préchargement des images:", error);
  }
};

const LoadingPlaceholder = ({ height = '400px', message = 'Chargement en cours...' }) => (
  <div className="w-full py-12 flex justify-center items-center" style={{ height }}>
    <div className="flex flex-col items-center">
      <div className="h-8 w-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      <p className="mt-4 text-muted-foreground">{message}</p>
    </div>
  </div>
);

interface FormattedClip {
  id: string | number;
  title: string;
  playlistName: string;
  thumbnailUrl: string;
  duration: string;
}

const LamourCestSurcotePage: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [clips, setClips] = useState<FormattedClip[]>([]);
  const { formatClipsCallback } = useClipsFormatter();
  
  useEffect(() => {
    prefetchCriticalAssets();
    
    const fetchData = async () => {
      try {
        console.log("📑 Démarrage de la récupération des données pour la playlist:", LAMOUR_SURCOTE_PLAYLIST_ID);
        
        const clipsData = await clipsApi.getClipsByPlaylistId(LAMOUR_SURCOTE_PLAYLIST_ID);
        console.log("🎬 Clips récupérés:", clipsData?.length || 0);
        
        if (clipsData && Array.isArray(clipsData)) {
          const formattedClips = formatClipsCallback(clipsData) as FormattedClip[];
          console.log("🎬 Clips formatés:", formattedClips.length);
          setClips(formattedClips);
        }
      } catch (error) {
        console.error('❌ Erreur lors de la récupération des données:', error);
        toast.error("Erreur lors du chargement des clips. Veuillez réessayer.");
      } finally {
        setIsLoaded(true);
      }
    };

    fetchData();
  }, [formatClipsCallback]);

  const challengeData = {
    title: "Challenge Créateurs : L'amour c'est surcoté",
    description: "Fais exploser ta visibilité sur TikTok avec StudioCanal et ReCut ! Crée des shorts viraux à partir d'extraits exclusifs du film événement « L'amour c'est surcoté » avec Hakim Jemili et Laura Felpin. Gagne en notoriété, fais monter ton nombre de vues et monétise enfin ton talent de remixeur !",
    rightHolderLogo: "/lovable-uploads/dafac05b-8f8c-4fd5-bcb4-507da388ac21.png",
    moviePoster: "/lovable-uploads/0259c719-1daa-46b5-bd60-cf6e7ee472b9.png",
    platform: "TikTok",
    cashPrize: "4000€",
    cpmPayout: "0.75€ par 1000 vues",
    startDate: "25 avril 2025 à 8h GMT",
    isCompleted: true,
    completionDate: "6 mai 2025"
  };
  
  return (
    <Layout sidebarDefaultOpen={false}>
      <SEO 
        title="L'amour c'est surcoté | Challenge créatif" 
        description="Participez au challenge vidéo 'L'amour c'est surcoté' et gagnez jusqu'à 4000€ pour vos créations" 
        keywords="challenge vidéo, tiktok, recut, amour, cinéma, monétisation" 
        ogImage="/lovable-uploads/0259c719-1daa-46b5-bd60-cf6e7ee472b9.png" 
      />
      <div className="min-h-screen relative">
        <div className="fixed inset-0 bg-gradient-to-br from-background via-background to-secondary/5 -z-10"></div>
        
        <div className="relative z-10 will-change-transform">
          <ChallengeHero 
            title={challengeData.title} 
            description={challengeData.description} 
            rightHolderLogo={challengeData.rightHolderLogo} 
            moviePoster={challengeData.moviePoster} 
            platform={challengeData.platform} 
            cashPrize={challengeData.cashPrize} 
            cpmPayout={challengeData.cpmPayout} 
            startDate={challengeData.startDate}
            isCompleted={challengeData.isCompleted}
            completionDate={challengeData.completionDate}
          />
          
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            {/* Stats Section avec id pour l'ancre */}
            <StatisticsSection />
            
            <div id="derivatives" className="scroll-mt-24">
              <SimpleDerivativesShowcase playlistId={LAMOUR_SURCOTE_PLAYLIST_ID} />
            </div>
            
            {clips.length > 0 ? (
              <div className="py-16">
                <div className="container mx-auto px-4">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Les extraits originaux du film</h2>
                  <p className="text-muted-foreground mb-8 text-center max-w-2xl mx-auto">
                    Découvrez les extraits exclusifs du film disponibles pour créer vos remix
                  </p>
                  <ContentCarousel
                    items={clips}
                    renderItem={(clip: FormattedClip) => (
                      <ClipCard key={clip.id} clip={clip} />
                    )}
                  />
                </div>
              </div>
            ) : (
              <LoadingPlaceholder message="Chargement des extraits originaux..." />
            )}
            
            <AboutSection />
            
            <TestimonialsSection />
            
            <div id="how-it-works" className="scroll-mt-24">
              <HowItWorksSection />
            </div>
          </div>
        </div>
        
        <Toaster />
      </div>
    </Layout>
  );
};

export default LamourCestSurcotePage;
